<script setup lang="ts">
import { ZhButton, ZhEmptyState, ZhPanel, ZhSectionHeading, ZhStatusPill } from '@zerohuman/ui'
import { computed } from 'vue'
import type { TeamSetupItemView } from '@/lib/teamSetup'

/**
 * The one thing setup asks for now (#1739): the current item of the server-held team setup list, with its plain-words
 * reason, and a Connect button when it is a connection. Nothing of the items still to come is named, so the founder
 * meets them one at a time, in the order the server serves them. With nothing left, it says so.
 */
const props = defineProps<{ current: TeamSetupItemView | null; remaining: number }>()
const emit = defineEmits<{ connect: [target: string] }>()

/** Asks to open the connect flow for the current item's tool type. */
function connect(): void {
    const target = props.current?.connect
    if (target) {
        emit('connect', target)
    }
}

/** How far along the founder is, without naming what is still to come. */
const left = computed(() =>
    props.remaining <= 1 ? 'The last thing to set up.' : `${props.remaining} things left to set up.`,
)
</script>

<template>
    <ZhEmptyState v-if="!current" label="Nothing needed" data-testid="team-setup-empty">
        Your team has everything it needs to start.
    </ZhEmptyState>
    <ZhPanel v-else class="space-y-3" data-testid="team-setup-item" :data-kind="current.kind">
        <div class="flex flex-wrap items-center gap-2">
            <ZhSectionHeading as="h2">{{ current.name }}</ZhSectionHeading>
            <ZhStatusPill v-if="current.connect" tone="action" :pulse="false">Not connected</ZhStatusPill>
            <ZhStatusPill v-else tone="neutral" :pulse="false">To do</ZhStatusPill>
        </div>
        <p class="text-sm">{{ current.reason }}</p>
        <p class="text-muted-foreground text-xs">{{ left }}</p>
        <ZhButton v-if="current.connect" data-testid="team-setup-connect" @click="connect">
            Connect
        </ZhButton>
    </ZhPanel>
</template>
